/**
 * CRM hook — API wrapper with snake_case ↔ camelCase transformation.
 * Uses apiClient.fetch() pattern consistent with useOrders and other hooks.
 */

import { useCallback } from 'react'
import { apiClient } from '@/services/apiClient'
import { useCrmStore } from '@/stores/crmStore'
import type {
  CrmContactSummary,
  CrmContactDetail,
  CrmContactUpdate,
  CrmContactAddress,
  TimelineEvent,
  ContactFilters,
  Pipeline,
  PipelineStage,
  Deal,
  DealCreate,
  PipelineStats,
  Segment,
  SegmentCreate,
  FilterGroup,
  Activity,
  ActivityCreate,
  ActivityType,
  AiSummary,
  SuggestedAction,
  ContactScore,
  DealScore,
  Company,
  CompanyCreate,
  Automation,
  AutomationTemplate,
  AiInsights,
  MergePreview,
} from '@/types/crm'

// ── Transform helpers ───────────────────────────────────────────────────────

function transformContactSummary(raw: Record<string, unknown>): CrmContactSummary {
  return {
    id: raw.id as string,
    channel: raw.channel as string,
    channelUserId: raw.channel_user_id as string,
    displayName: raw.display_name as string | null,
    isBlocked: (raw.is_blocked as boolean) ?? false,
    isDeleted: (raw.is_deleted as boolean) ?? false,
    tags: (raw.tags as string[]) ?? [],
    totalOrders: (raw.total_orders as number) ?? 0,
    totalSpentCents: (raw.total_spent_cents as number) ?? 0,
    currency: (raw.currency as string) ?? 'USD',
    averageOrderCents: (raw.average_order_cents as number) ?? 0,
    lastOrderAt: raw.last_order_at as string | null,
    email: raw.email as string | null,
    languagePreference: raw.language_preference as string | null,
    loyaltyTier: raw.loyalty_tier as string | null,
    firstSeenAt: raw.first_seen_at as string | null,
    lastSeenAt: raw.last_seen_at as string | null,
    conversationCount: (raw.conversation_count as number) ?? 0,
  }
}

function transformAddress(raw: Record<string, unknown>): CrmContactAddress {
  return {
    id: raw.id as string,
    label: (raw.label as string) ?? null,
    rawText: raw.raw_text as string,
    formatted: (raw.formatted as string) ?? null,
    lat: (raw.lat as number) ?? null,
    lng: (raw.lng as number) ?? null,
    components: (raw.components as unknown[]) ?? null,
    isDefault: Boolean(raw.is_default),
    source: (raw.source as 'order' | 'staff') ?? 'staff',
    sourceOrderId: (raw.source_order_id as string) ?? null,
    lastUsedAt: (raw.last_used_at as string) ?? null,
    createdAt: (raw.created_at as string) ?? null,
  }
}

function transformContactDetail(raw: Record<string, unknown>): CrmContactDetail {
  return {
    id: raw.id as string,
    channel: raw.channel as string,
    channelUserId: raw.channel_user_id as string,
    displayName: raw.display_name as string | null,
    userId: raw.user_id as string | null,
    isBlocked: (raw.is_blocked as boolean) ?? false,
    isVerified: (raw.is_verified as boolean) ?? false,
    isDeleted: (raw.is_deleted as boolean) ?? false,
    deletedAt: raw.deleted_at as string | null,
    notes: raw.notes as string | null,
    tags: (raw.tags as string[]) ?? [],
    defaultPersonaId: raw.default_persona_id as string | null,
    metadata: raw.metadata as Record<string, unknown> | null,
    totalOrders: (raw.total_orders as number) ?? 0,
    totalSpentCents: (raw.total_spent_cents as number) ?? 0,
    currency: (raw.currency as string) ?? 'USD',
    averageOrderCents: (raw.average_order_cents as number) ?? 0,
    lastOrderAt: raw.last_order_at as string | null,
    lastOrderId: raw.last_order_id as string | null,
    email: raw.email as string | null,
    birthday: raw.birthday as string | null,
    languagePreference: raw.language_preference as string | null,
    aiInsights: raw.ai_insights as CrmContactDetail['aiInsights'],
    addresses: ((raw.addresses as Array<Record<string, unknown>>) ?? []).map(transformAddress),
    loyaltyTier: raw.loyalty_tier as string | null,
    loyaltyPoints: (raw.loyalty_points as number) ?? 0,
    loyaltyEnrolledAt: raw.loyalty_enrolled_at as string | null,
    fiscalData: raw.fiscal_data as Record<string, unknown> | null,
    firstSeenAt: raw.first_seen_at as string | null,
    lastSeenAt: raw.last_seen_at as string | null,
    conversationCount: (raw.conversation_count as number) ?? 0,
  }
}

function transformMergePreview(raw: Record<string, unknown>): MergePreview {
  const mapField = (f: Record<string, unknown>) => ({
    field: f.field as string,
    primary: f.primary ?? null,
    secondary: f.secondary ?? null,
    result: f.result ?? null,
  })
  const rawInsights = raw.ai_insights as Record<string, unknown> | undefined
  return {
    primaryId: raw.primary_id as string,
    secondaryId: raw.secondary_id as string,
    primaryName: (raw.primary_name as string | null) ?? null,
    secondaryName: (raw.secondary_name as string | null) ?? null,
    fields: ((raw.fields as Record<string, unknown>[]) ?? []).map(mapField),
    aiInsights: rawInsights
      ? mapField(rawInsights)
      : { field: 'ai_insights', primary: null, secondary: null, result: null },
    reassigned: (raw.reassigned as Record<string, number>) ?? {},
    secondaryFate: (raw.secondary_fate as string) ?? 'soft_deleted',
  }
}

function transformTimelineEvent(raw: Record<string, unknown>): TimelineEvent {
  return {
    id: raw.id as string,
    eventType: raw.event_type as string,
    title: raw.title as string,
    description: raw.description as string | null,
    sourceType: raw.source_type as string | null,
    sourceId: raw.source_id as string | null,
    metadata: raw.metadata as Record<string, unknown> | null,
    icon: raw.icon as string | null,
    color: raw.color as string | null,
    actorName: raw.actor_name as string | null,
    actorType: raw.actor_type as string | null,
    createdAt: raw.created_at as string,
  }
}

function transformPipelineStage(raw: Record<string, unknown>): PipelineStage {
  return {
    id: raw.id as string,
    name: raw.name as string,
    order: (raw.order as number) ?? 0,
    probability: (raw.probability as number) ?? 0,
    color: (raw.color as string) ?? '#6B7280',
    isWon: (raw.is_won as boolean) ?? false,
    isLost: (raw.is_lost as boolean) ?? false,
  }
}

function transformPipeline(raw: Record<string, unknown>): Pipeline {
  return {
    id: raw.id as string,
    name: raw.name as string,
    description: raw.description as string | null,
    stages: ((raw.stages as Array<Record<string, unknown>>) ?? []).map(transformPipelineStage),
    isDefault: (raw.is_default as boolean) ?? false,
    isActive: (raw.is_active as boolean) ?? true,
    currency: raw.currency as string | null,
    autoCreateFromOrders: (raw.auto_create_from_orders as boolean) ?? false,
    autoCreateStageId: raw.auto_create_stage_id as string | null,
    createdAt: raw.created_at as string | null,
    updatedAt: raw.updated_at as string | null,
  }
}

function transformDeal(raw: Record<string, unknown>): Deal {
  const history = raw.stage_history as Array<Record<string, unknown>> | null
  return {
    id: raw.id as string,
    pipelineId: raw.pipeline_id as string,
    contactId: raw.contact_id as string | null,
    companyId: raw.company_id as string | null,
    orderId: raw.order_id as string | null,
    name: raw.name as string,
    stageId: raw.stage_id as string,
    amountCents: raw.amount_cents as number | null,
    currency: raw.currency as string | null,
    probability: raw.probability as number | null,
    expectedCloseDate: raw.expected_close_date as string | null,
    actualCloseDate: raw.actual_close_date as string | null,
    stageEnteredAt: raw.stage_entered_at as string | null,
    stageHistory: history?.map((h) => ({
      stageId: h.stage_id as string,
      stageName: h.stage_name as string | null,
      enteredAt: h.entered_at as string,
      exitedAt: h.exited_at as string | null,
    })) ?? null,
    source: raw.source as string | null,
    sourceDetail: raw.source_detail as string | null,
    lossReason: raw.loss_reason as string | null,
    tags: raw.tags as string[] | null,
    notes: raw.notes as string | null,
    customFields: raw.custom_fields as Record<string, unknown> | null,
    assignedTo: raw.assigned_to as string | null,
    isDeleted: (raw.is_deleted as boolean) ?? false,
    createdAt: raw.created_at as string | null,
    updatedAt: raw.updated_at as string | null,
    wonAt: raw.won_at as string | null,
    lostAt: raw.lost_at as string | null,
    contactName: raw.contact_name as string | null,
    pipelineName: raw.pipeline_name as string | null,
    stageName: raw.stage_name as string | null,
    stageColor: raw.stage_color as string | null,
  }
}

function transformPipelineStats(raw: Record<string, unknown>): PipelineStats {
  return {
    pipelineId: raw.pipeline_id as string,
    pipelineName: raw.pipeline_name as string,
    currency: raw.currency as string | null,
    stages: ((raw.stages as Array<Record<string, unknown>>) ?? []).map((s) => ({
      stageId: s.stage_id as string,
      stageName: s.stage_name as string,
      color: s.color as string,
      count: s.count as number,
      totalCents: s.total_cents as number,
      weightedCents: s.weighted_cents as number,
    })),
    totalDeals: raw.total_deals as number,
    totalValueCents: raw.total_value_cents as number,
    weightedValueCents: raw.weighted_value_cents as number,
  }
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useCRM() {
  const fetchContacts = useCallback(async (filters?: Partial<ContactFilters>) => {
    const currentFilters = { ...useCrmStore.getState().contactFilters, ...filters }
    useCrmStore.getState().setLoadingContacts(true)

    try {
      const params = new URLSearchParams()
      if (currentFilters.search) params.set('search', currentFilters.search)
      if (currentFilters.channel) params.set('channel', currentFilters.channel)
      if (currentFilters.tag) params.set('tag', currentFilters.tag)
      if (currentFilters.blocked !== null && currentFilters.blocked !== undefined) {
        params.set('blocked', String(currentFilters.blocked))
      }
      if (currentFilters.includeDeleted) params.set('include_deleted', 'true')
      params.set('sort_by', currentFilters.sortBy)
      params.set('sort_order', currentFilters.sortOrder)
      params.set('page', String(currentFilters.page))
      params.set('per_page', String(currentFilters.perPage))

      const res = await apiClient.fetch(`/api/crm/contacts?${params.toString()}`)
      if (!res.ok) throw new Error('Failed to fetch contacts')
      const data = await res.json()

      const contacts = ((data.contacts as Array<Record<string, unknown>>) ?? []).map(
        transformContactSummary
      )
      useCrmStore.getState().setContacts(
        contacts,
        data.total as number,
        data.total_pages as number
      )
    } catch (error) {
      console.error('Failed to fetch CRM contacts:', error)
    } finally {
      useCrmStore.getState().setLoadingContacts(false)
    }
  }, [])

  const searchContacts = useCallback(async (search: string): Promise<CrmContactSummary[]> => {
    try {
      const response = await apiClient.fetch(
        `/api/crm/contacts?search=${encodeURIComponent(search)}&per_page=20`,
      )
      if (!response.ok) return []
      const data = await response.json()
      return ((data.contacts as Record<string, unknown>[]) ?? []).map(transformContactSummary)
    } catch {
      return []
    }
  }, [])

  const fetchMergePreview = useCallback(
    async (primaryId: string, secondaryId: string): Promise<MergePreview | null> => {
      try {
        const response = await apiClient.fetch(
          `/api/crm/contacts/merge/preview?primary_id=${primaryId}&secondary_id=${secondaryId}`,
        )
        if (!response.ok) return null
        return transformMergePreview(await response.json())
      } catch {
        return null
      }
    },
    [],
  )

  const mergeContacts = useCallback(
    async (primaryId: string, secondaryId: string): Promise<string | null> => {
      try {
        const response = await apiClient.fetch(
          `/api/crm/contacts/merge?primary_id=${primaryId}&secondary_id=${secondaryId}`,
          { method: 'POST' },
        )
        if (!response.ok) return null
        const data = await response.json()
        return (data.primary_id as string) ?? null
      } catch {
        return null
      }
    },
    [],
  )

  const fetchContactDetail = useCallback(async (contactId: string) => {
    useCrmStore.getState().setLoadingDetail(true)
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}`)
      if (!res.ok) throw new Error('Contact not found')
      const data = await res.json()
      const contact = transformContactDetail(data as Record<string, unknown>)
      useCrmStore.getState().setSelectedContact(contact)
      return contact
    } catch (error) {
      console.error('Failed to fetch contact detail:', error)
      return null
    } finally {
      useCrmStore.getState().setLoadingDetail(false)
    }
  }, [])

  const updateContact = useCallback(async (contactId: string, updates: CrmContactUpdate) => {
    try {
      // Convert camelCase to snake_case for API
      const body: Record<string, unknown> = {}
      if (updates.displayName !== undefined) body.display_name = updates.displayName
      if (updates.notes !== undefined) body.notes = updates.notes
      if (updates.tags !== undefined) body.tags = updates.tags
      if (updates.isBlocked !== undefined) body.is_blocked = updates.isBlocked
      if (updates.email !== undefined) body.email = updates.email
      if (updates.birthday !== undefined) body.birthday = updates.birthday
      if (updates.languagePreference !== undefined) body.language_preference = updates.languagePreference

      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to update contact')
      const data = await res.json()
      const contact = transformContactDetail(data as Record<string, unknown>)
      useCrmStore.getState().setSelectedContact(contact)
      return contact
    } catch (error) {
      console.error('Failed to update contact:', error)
      return null
    }
  }, [])

  const updateContactInsights = useCallback(async (contactId: string, updates: AiInsights) => {
    try {
      // Insight keys are identical in snake_case and camelCase — send verbatim
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/insights`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      if (!res.ok) throw new Error('Failed to update insights')
      const data = await res.json()
      const contact = transformContactDetail(data as Record<string, unknown>)
      useCrmStore.getState().setSelectedContact(contact)
      return contact
    } catch (error) {
      console.error('Failed to update insights:', error)
      return null
    }
  }, [])

  const addContactAddress = useCallback(
    async (contactId: string, payload: { rawText: string; label?: string; isDefault?: boolean }) => {
      try {
        const body: Record<string, unknown> = { raw_text: payload.rawText }
        if (payload.label !== undefined) body.label = payload.label
        if (payload.isDefault !== undefined) body.is_default = payload.isDefault
        const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/addresses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        if (!res.ok) throw new Error('Failed to add address')
        const detail = transformContactDetail(await res.json())
        useCrmStore.getState().setSelectedContact(detail)
        return detail
      } catch (error) {
        console.error('Failed to add address:', error)
        return null
      }
    },
    [],
  )

  const updateContactAddress = useCallback(
    async (
      contactId: string,
      addressId: string,
      payload: { rawText?: string; label?: string; isDefault?: boolean },
    ) => {
      try {
        const body: Record<string, unknown> = {}
        if (payload.rawText !== undefined) body.raw_text = payload.rawText
        if (payload.label !== undefined) body.label = payload.label
        if (payload.isDefault !== undefined) body.is_default = payload.isDefault
        const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/addresses/${addressId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        if (!res.ok) throw new Error('Failed to update address')
        const detail = transformContactDetail(await res.json())
        useCrmStore.getState().setSelectedContact(detail)
        return detail
      } catch (error) {
        console.error('Failed to update address:', error)
        return null
      }
    },
    [],
  )

  const deleteContactAddress = useCallback(async (contactId: string, addressId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/addresses/${addressId}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Failed to delete address')
      const detail = transformContactDetail(await res.json())
      useCrmStore.getState().setSelectedContact(detail)
      return detail
    } catch (error) {
      console.error('Failed to delete address:', error)
      return null
    }
  }, [])

  const blockContact = useCallback(async (contactId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/block`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to block contact')
      const contact = transformContactDetail(await res.json() as Record<string, unknown>)
      useCrmStore.getState().setSelectedContact(contact)
      return contact
    } catch (error) {
      console.error('Failed to block contact:', error)
      return null
    }
  }, [])

  const unblockContact = useCallback(async (contactId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/unblock`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to unblock contact')
      const contact = transformContactDetail(await res.json() as Record<string, unknown>)
      useCrmStore.getState().setSelectedContact(contact)
      return contact
    } catch (error) {
      console.error('Failed to unblock contact:', error)
      return null
    }
  }, [])

  const deleteContact = useCallback(async (contactId: string, policy: 'soft_delete' | 'anonymize') => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}?policy=${policy}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete contact')
      const contact = transformContactDetail(await res.json() as Record<string, unknown>)
      useCrmStore.getState().setSelectedContact(contact)
      return contact
    } catch (error) {
      console.error('Failed to delete contact:', error)
      return null
    }
  }, [])

  const restoreContact = useCallback(async (contactId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/restore`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to restore contact')
      const contact = transformContactDetail(await res.json() as Record<string, unknown>)
      useCrmStore.getState().setSelectedContact(contact)
      return contact
    } catch (error) {
      console.error('Failed to restore contact:', error)
      return null
    }
  }, [])

  const fetchTimeline = useCallback(async (
    contactId: string,
    options?: { eventTypes?: string; before?: string; limit?: number }
  ) => {
    useCrmStore.getState().setLoadingTimeline(true)
    try {
      const params = new URLSearchParams()
      if (options?.eventTypes) params.set('event_types', options.eventTypes)
      if (options?.before) params.set('before', options.before)
      if (options?.limit) params.set('limit', String(options.limit))

      const res = await apiClient.fetch(
        `/api/crm/contacts/${contactId}/timeline?${params.toString()}`
      )
      if (!res.ok) throw new Error('Failed to fetch timeline')
      const data = await res.json()

      const events = ((data.events as Array<Record<string, unknown>>) ?? []).map(
        transformTimelineEvent
      )
      const hasMore = (data.has_more as boolean) ?? false
      const nextCursor = data.next_cursor as string | null

      if (options?.before) {
        // Appending for infinite scroll
        useCrmStore.getState().appendTimelineEvents(events, hasMore, nextCursor)
      } else {
        // Fresh load
        useCrmStore.getState().setTimelineEvents(events, hasMore, nextCursor)
      }

      return { events, hasMore, nextCursor }
    } catch (error) {
      console.error('Failed to fetch timeline:', error)
      return { events: [], hasMore: false, nextCursor: null }
    } finally {
      useCrmStore.getState().setLoadingTimeline(false)
    }
  }, [])

  // ── Deal methods ─────────────────────────────────────────────────────────

  const fetchPipelines = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/crm/pipelines')
      if (!res.ok) throw new Error('Failed to fetch pipelines')
      const data = await res.json()
      const pipelines = (data as Array<Record<string, unknown>>).map(transformPipeline)
      useCrmStore.getState().setPipelines(pipelines)
      // Set active pipeline to first if none selected
      if (!useCrmStore.getState().activePipeline && pipelines.length > 0) {
        const defaultPipeline = pipelines.find((p) => p.isDefault) || pipelines[0]
        useCrmStore.getState().setActivePipeline(defaultPipeline)
      }
      return pipelines
    } catch (error) {
      console.error('Failed to fetch pipelines:', error)
      return []
    }
  }, [])

  const fetchDeals = useCallback(async (pipelineId?: string) => {
    useCrmStore.getState().setLoadingDeals(true)
    try {
      const params = new URLSearchParams()
      const pid = pipelineId || useCrmStore.getState().activePipeline?.id
      if (pid) params.set('pipeline_id', pid)
      params.set('per_page', '500') // Kanban loads all deals for the pipeline

      const res = await apiClient.fetch(`/api/crm/deals?${params.toString()}`)
      if (!res.ok) throw new Error('Failed to fetch deals')
      const data = await res.json()
      const deals = ((data.deals as Array<Record<string, unknown>>) ?? []).map(transformDeal)
      useCrmStore.getState().setDeals(deals, data.total as number)
      return deals
    } catch (error) {
      console.error('Failed to fetch deals:', error)
      return []
    } finally {
      useCrmStore.getState().setLoadingDeals(false)
    }
  }, [])

  const createDeal = useCallback(async (data: DealCreate) => {
    try {
      const body: Record<string, unknown> = {
        name: data.name,
        pipeline_id: data.pipelineId,
        stage_id: data.stageId,
      }
      if (data.contactId) body.contact_id = data.contactId
      if (data.amountCents !== undefined) body.amount_cents = data.amountCents
      if (data.currency) body.currency = data.currency
      if (data.expectedCloseDate) body.expected_close_date = data.expectedCloseDate
      if (data.source) body.source = data.source
      if (data.notes) body.notes = data.notes
      if (data.tags) body.tags = data.tags

      const res = await apiClient.fetch('/api/crm/deals', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to create deal')
      const raw = await res.json()
      const deal = transformDeal(raw as Record<string, unknown>)
      // Refresh deal list
      await fetchDeals()
      return deal
    } catch (error) {
      console.error('Failed to create deal:', error)
      return null
    }
  }, [fetchDeals])

  const moveDealStage = useCallback(async (dealId: string, stageId: string, lossReason?: string) => {
    try {
      const body: Record<string, unknown> = { stage_id: stageId }
      if (lossReason) body.loss_reason = lossReason

      const res = await apiClient.fetch(`/api/crm/deals/${dealId}/stage`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to move deal')
      const raw = await res.json()
      const deal = transformDeal(raw as Record<string, unknown>)
      useCrmStore.getState().updateDealInList(deal)
      return deal
    } catch (error) {
      console.error('Failed to move deal:', error)
      return null
    }
  }, [])

  const fetchPipelineStats = useCallback(async (pipelineId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/pipelines/${pipelineId}/stats`)
      if (!res.ok) throw new Error('Failed to fetch pipeline stats')
      const raw = await res.json()
      const stats = transformPipelineStats(raw as Record<string, unknown>)
      useCrmStore.getState().setPipelineStats(stats)
      return stats
    } catch (error) {
      console.error('Failed to fetch pipeline stats:', error)
      return null
    }
  }, [])

  // ── Segment methods ──────────────────────────────────────────────────────

  const fetchSegments = useCallback(async () => {
    useCrmStore.getState().setLoadingSegments(true)
    try {
      const res = await apiClient.fetch('/api/crm/segments')
      if (!res.ok) throw new Error('Failed to fetch segments')
      const data = await res.json()
      const segments: Segment[] = (data as Array<Record<string, unknown>>).map((raw) => ({
        id: raw.id as string,
        name: raw.name as string,
        description: raw.description as string | null,
        color: raw.color as string | null,
        icon: raw.icon as string | null,
        filters: raw.filters as FilterGroup,
        isSystem: (raw.is_system as boolean) ?? false,
        isPinned: (raw.is_pinned as boolean) ?? false,
        cachedCount: (raw.cached_count as number) ?? 0,
        cachedAt: raw.cached_at as string | null,
        createdAt: raw.created_at as string | null,
        updatedAt: raw.updated_at as string | null,
      }))
      useCrmStore.getState().setSegments(segments)
      return segments
    } catch (error) {
      console.error('Failed to fetch segments:', error)
      return []
    } finally {
      useCrmStore.getState().setLoadingSegments(false)
    }
  }, [])

  const createSegment = useCallback(async (data: SegmentCreate) => {
    try {
      const body: Record<string, unknown> = {
        name: data.name,
        filters: data.filters,
      }
      if (data.description) body.description = data.description
      if (data.color) body.color = data.color
      if (data.icon) body.icon = data.icon
      if (data.isPinned !== undefined) body.is_pinned = data.isPinned

      const res = await apiClient.fetch('/api/crm/segments', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to create segment')
      await fetchSegments()
      return true
    } catch (error) {
      console.error('Failed to create segment:', error)
      return false
    }
  }, [fetchSegments])

  const deleteSegment = useCallback(async (segmentId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/segments/${segmentId}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete segment')
      await fetchSegments()
      return true
    } catch (error) {
      console.error('Failed to delete segment:', error)
      return false
    }
  }, [fetchSegments])

  const toggleSegmentPin = useCallback(async (segmentId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/segments/${segmentId}/pin`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to toggle pin')
      await fetchSegments()
    } catch (error) {
      console.error('Failed to toggle pin:', error)
    }
  }, [fetchSegments])

  const fetchSegmentContacts = useCallback(async (segmentId: string, page: number = 1) => {
    useCrmStore.getState().setLoadingContacts(true)
    try {
      const res = await apiClient.fetch(`/api/crm/segments/${segmentId}/contacts?page=${page}&per_page=50`)
      if (!res.ok) throw new Error('Failed to fetch segment contacts')
      const data = await res.json()
      const contacts = ((data.contacts as Array<Record<string, unknown>>) ?? []).map(transformContactSummary)
      useCrmStore.getState().setContacts(contacts, data.total as number, data.total_pages as number)
    } catch (error) {
      console.error('Failed to fetch segment contacts:', error)
    } finally {
      useCrmStore.getState().setLoadingContacts(false)
    }
  }, [])

  // ── Activity methods ─────────────────────────────────────────────────────

  const transformActivity = (raw: Record<string, unknown>): Activity => ({
    id: raw.id as string,
    activityType: raw.activity_type as ActivityType,
    title: raw.title as string | null,
    description: raw.description as string | null,
    contactId: raw.contact_id as string | null,
    dealId: raw.deal_id as string | null,
    companyId: raw.company_id as string | null,
    isCompleted: (raw.is_completed as boolean) ?? false,
    completedAt: raw.completed_at as string | null,
    dueDate: raw.due_date as string | null,
    priority: (raw.priority as string) ?? 'normal',
    durationMinutes: raw.duration_minutes as number | null,
    outcome: raw.outcome as string | null,
    channel: raw.channel as string | null,
    direction: raw.direction as string | null,
    sentiment: raw.sentiment as string | null,
    aiSummary: raw.ai_summary as string | null,
    assignedTo: raw.assigned_to as string | null,
    createdBy: raw.created_by as string | null,
    createdAt: raw.created_at as string | null,
    updatedAt: raw.updated_at as string | null,
    contactName: raw.contact_name as string | null,
    dealName: raw.deal_name as string | null,
  })

  const fetchActivities = useCallback(async (filters?: {
    activityType?: string; contactId?: string; dealId?: string;
    isCompleted?: boolean; page?: number
  }) => {
    useCrmStore.getState().setLoadingActivities(true)
    try {
      const params = new URLSearchParams()
      if (filters?.activityType) params.set('activity_type', filters.activityType)
      if (filters?.contactId) params.set('contact_id', filters.contactId)
      if (filters?.dealId) params.set('deal_id', filters.dealId)
      if (filters?.isCompleted !== undefined) params.set('is_completed', String(filters.isCompleted))
      params.set('page', String(filters?.page || 1))

      const res = await apiClient.fetch(`/api/crm/activities?${params.toString()}`)
      if (!res.ok) throw new Error('Failed to fetch activities')
      const data = await res.json()
      const activities = ((data.activities as Array<Record<string, unknown>>) ?? []).map(transformActivity)
      useCrmStore.getState().setActivities(activities, data.total as number)
      return activities
    } catch (error) {
      console.error('Failed to fetch activities:', error)
      return []
    } finally {
      useCrmStore.getState().setLoadingActivities(false)
    }
  }, [])

  const createActivity = useCallback(async (data: ActivityCreate) => {
    try {
      const body: Record<string, unknown> = {
        activity_type: data.activityType,
      }
      if (data.title) body.title = data.title
      if (data.description) body.description = data.description
      if (data.contactId) body.contact_id = data.contactId
      if (data.dealId) body.deal_id = data.dealId
      if (data.dueDate) body.due_date = data.dueDate
      if (data.priority) body.priority = data.priority

      const res = await apiClient.fetch('/api/crm/activities', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Failed to create activity')
      const raw = await res.json()
      return transformActivity(raw as Record<string, unknown>)
    } catch (error) {
      console.error('Failed to create activity:', error)
      return null
    }
  }, [])

  const completeActivity = useCallback(async (activityId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/activities/${activityId}/complete`, { method: 'PUT' })
      if (!res.ok) throw new Error('Failed to complete activity')
      const raw = await res.json()
      return transformActivity(raw as Record<string, unknown>)
    } catch (error) {
      console.error('Failed to complete activity:', error)
      return null
    }
  }, [])

  const fetchOverdueCount = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/crm/activities/overdue-count')
      if (!res.ok) return
      const data = await res.json()
      useCrmStore.getState().setOverdueCount((data.count as number) ?? 0)
    } catch {
      // Non-critical
    }
  }, [])

  // ── AI methods ───────────────────────────────────────────────────────────

  const fetchAiSummary = useCallback(async (contactId: string): Promise<AiSummary | null> => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/ai-summary`)
      if (!res.ok) return null
      const raw = await res.json()
      return {
        contactId: raw.contact_id as string,
        summary: raw.summary as string,
        generatedAt: raw.generated_at as string,
        isCached: (raw.is_cached as boolean) ?? false,
      }
    } catch { return null }
  }, [])

  const fetchSuggestedActions = useCallback(async (contactId: string): Promise<SuggestedAction[]> => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/suggested-actions`)
      if (!res.ok) return []
      const raw = await res.json()
      return ((raw.suggestions as Array<Record<string, unknown>>) ?? []).map((s) => ({
        id: s.id as string,
        title: s.title as string,
        description: s.description as string | null,
        actionType: s.action_type as string,
        priority: s.priority as string,
        reason: s.reason as string,
        contactId: s.contact_id as string | null,
        dealId: s.deal_id as string | null,
      }))
    } catch { return [] }
  }, [])

  const fetchContactScore = useCallback(async (contactId: string): Promise<ContactScore | null> => {
    try {
      const res = await apiClient.fetch(`/api/crm/contacts/${contactId}/score`)
      if (!res.ok) return null
      const raw = await res.json()
      return {
        contactId: raw.contact_id as string,
        totalScore: raw.total_score as number,
        components: ((raw.components as Array<Record<string, unknown>>) ?? []).map((c) => ({
          name: c.name as string,
          score: c.score as number,
          weight: c.weight as number,
          description: c.description as string | null,
        })),
        sentiment: raw.sentiment as string | null,
        sentimentTrend: raw.sentiment_trend as string | null,
      }
    } catch { return null }
  }, [])

  const fetchDealScore = useCallback(async (dealId: string): Promise<DealScore | null> => {
    try {
      const res = await apiClient.fetch(`/api/crm/deals/${dealId}/score`)
      if (!res.ok) return null
      const raw = await res.json()
      return {
        dealId: raw.deal_id as string,
        totalScore: raw.total_score as number,
        components: ((raw.components as Array<Record<string, unknown>>) ?? []).map((c) => ({
          name: c.name as string,
          score: c.score as number,
          weight: c.weight as number,
          description: c.description as string | null,
        })),
      }
    } catch { return null }
  }, [])

  // ── Company methods ──────────────────────────────────────────────────────

  const transformCompany = (raw: Record<string, unknown>): Company => ({
    id: raw.id as string,
    name: raw.name as string,
    domain: raw.domain as string | null,
    industry: raw.industry as string | null,
    size: raw.size as string | null,
    website: raw.website as string | null,
    phone: raw.phone as string | null,
    address: raw.address as string | null,
    city: raw.city as string | null,
    state: raw.state as string | null,
    country: raw.country as string | null,
    postalCode: raw.postal_code as string | null,
    tags: raw.tags as string[] | null,
    notes: raw.notes as string | null,
    fiscalData: raw.fiscal_data as Record<string, unknown> | null,
    customFields: raw.custom_fields as Record<string, unknown> | null,
    totalContacts: (raw.total_contacts as number) ?? 0,
    totalRevenueCents: (raw.total_revenue_cents as number) ?? 0,
    currency: raw.currency as string | null,
    isDeleted: (raw.is_deleted as boolean) ?? false,
    createdAt: raw.created_at as string | null,
    updatedAt: raw.updated_at as string | null,
    contacts: raw.contacts ? (raw.contacts as Array<Record<string, unknown>>).map((c) => ({
      id: c.id as string,
      displayName: c.display_name as string | null,
      channelUserId: c.channel_user_id as string,
      channel: c.channel as string,
      jobTitle: c.job_title as string | null,
      totalSpentCents: (c.total_spent_cents as number) ?? 0,
    })) : null,
  })

  const fetchCompanies = useCallback(async (search?: string, page: number = 1, sortBy?: string, sortOrder?: string) => {
    try {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      params.set('page', String(page))
      if (sortBy) params.set('sort_by', sortBy)
      if (sortOrder) params.set('sort_order', sortOrder)
      const res = await apiClient.fetch(`/api/crm/companies?${params.toString()}`)
      if (!res.ok) throw new Error('Failed to fetch companies')
      const data = await res.json()
      return {
        companies: ((data.companies as Array<Record<string, unknown>>) ?? []).map(transformCompany),
        total: data.total as number,
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error)
      return { companies: [], total: 0 }
    }
  }, [])

  const fetchCompanyDetail = useCallback(async (companyId: string): Promise<Company | null> => {
    try {
      const res = await apiClient.fetch(`/api/crm/companies/${companyId}`)
      if (!res.ok) return null
      const raw = await res.json()
      return transformCompany(raw as Record<string, unknown>)
    } catch { return null }
  }, [])

  const createCompany = useCallback(async (data: CompanyCreate): Promise<Company | null> => {
    try {
      const res = await apiClient.fetch('/api/crm/companies', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to create company')
      const raw = await res.json()
      return transformCompany(raw as Record<string, unknown>)
    } catch (error) {
      console.error('Failed to create company:', error)
      return null
    }
  }, [])

  const deleteCompany = useCallback(async (companyId: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/companies/${companyId}`, { method: 'DELETE' })
      return res.ok
    } catch { return false }
  }, [])

  // ── Automation methods ────────────────────────────────────────────────────

  const transformAutomation = (raw: Record<string, unknown>): Automation => ({
    id: raw.id as string,
    name: raw.name as string,
    description: raw.description as string | null,
    isActive: (raw.is_active as boolean) ?? false,
    trigger: raw.trigger as Automation['trigger'],
    actions: (raw.actions as Automation['actions']) ?? [],
    runCount: (raw.run_count as number) ?? 0,
    lastRunAt: raw.last_run_at as string | null,
    lastError: raw.last_error as string | null,
    createdAt: raw.created_at as string | null,
    updatedAt: raw.updated_at as string | null,
  })

  const fetchAutomations = useCallback(async (): Promise<Automation[]> => {
    try {
      const res = await apiClient.fetch('/api/crm/automations')
      if (!res.ok) return []
      const data = await res.json()
      return (data as Array<Record<string, unknown>>).map(transformAutomation)
    } catch { return [] }
  }, [])

  const createAutomation = useCallback(async (body: Record<string, unknown>): Promise<Automation | null> => {
    try {
      const res = await apiClient.fetch('/api/crm/automations', {
        method: 'POST', body: JSON.stringify(body),
      })
      if (!res.ok) return null
      return transformAutomation(await res.json() as Record<string, unknown>)
    } catch { return null }
  }, [])

  const toggleAutomation = useCallback(async (id: string) => {
    try {
      await apiClient.fetch(`/api/crm/automations/${id}/toggle`, { method: 'POST' })
    } catch { /* non-critical */ }
  }, [])

  const deleteAutomation = useCallback(async (id: string) => {
    try {
      const res = await apiClient.fetch(`/api/crm/automations/${id}`, { method: 'DELETE' })
      return res.ok
    } catch { return false }
  }, [])

  const fetchAutomationTemplates = useCallback(async (): Promise<AutomationTemplate[]> => {
    try {
      const res = await apiClient.fetch('/api/crm/automations/templates')
      if (!res.ok) return []
      return await res.json() as AutomationTemplate[]
    } catch { return [] }
  }, [])

  return {
    // Contacts
    fetchContacts,
    searchContacts,
    fetchMergePreview,
    mergeContacts,
    fetchContactDetail,
    updateContact,
    updateContactInsights,
    addContactAddress,
    updateContactAddress,
    deleteContactAddress,
    blockContact,
    unblockContact,
    deleteContact,
    restoreContact,
    fetchTimeline,
    // Deals
    fetchPipelines,
    fetchDeals,
    createDeal,
    moveDealStage,
    fetchPipelineStats,
    // Segments
    fetchSegments,
    createSegment,
    deleteSegment,
    toggleSegmentPin,
    fetchSegmentContacts,
    // Activities
    fetchActivities,
    createActivity,
    completeActivity,
    fetchOverdueCount,
    // AI
    fetchAiSummary,
    fetchSuggestedActions,
    fetchContactScore,
    fetchDealScore,
    // Companies
    fetchCompanies,
    fetchCompanyDetail,
    createCompany,
    deleteCompany,
    // Automations
    fetchAutomations,
    createAutomation,
    toggleAutomation,
    deleteAutomation,
    fetchAutomationTemplates,
  }
}
